// Fee Management System for CardsPay
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';

class FeeManager {
  constructor() {
    this.defaultFees = null;
    this.customerOverrides = {};
    this.listeners = [];
  }

  // Initialize and listen to fee changes
  async initialize() {
    const feeDoc = doc(db, 'feeSettings', 'cardspay-config');

    // Set up real-time listener
    const unsubscribe = onSnapshot(feeDoc, async (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        this.defaultFees = data.default || this.getDefaultStructure();

        // Process customer overrides - handle both underscore and dot formats
        const rawOverrides = data.customerOverrides || {};
        this.customerOverrides = {};
        let needsCleanup = false;
        const cleanedOverrides = {};

        Object.entries(rawOverrides).forEach(([key, value]) => {
          // Check if value has an email property (new format with underscore)
          if (value && value.email) {
            this.customerOverrides[value.email] = value;
            cleanedOverrides[key] = value;
          }
          // Handle underscore format
          else if (key.includes('_') && key.includes('@')) {
            const email = key.replace(/_/g, '.');
            this.customerOverrides[email] = { ...value, email };
            cleanedOverrides[key] = { ...value, email };
          }
          // Handle malformed keys (morebay1@gmail without proper format)
          else if (key.includes('@') && !key.includes('.') && !key.includes('_')) {
            needsCleanup = true;
            // Don't add to customerOverrides
          }
          // Normal email format (shouldn't happen due to Firebase restrictions)
          else if (key.includes('@') && key.includes('.')) {
            // Convert to underscore format for Firebase
            const firebaseKey = key.replace(/\./g, '_');
            this.customerOverrides[key] = { ...value, email: key };
            cleanedOverrides[firebaseKey] = { ...value, email: key };
            needsCleanup = true;
          }
          else {
            // Keep as-is
            cleanedOverrides[key] = value;
          }
        });

        // Auto-cleanup if needed
        if (needsCleanup) {
          try {
            await setDoc(doc.ref, {
              ...data,
              customerOverrides: cleanedOverrides
            });
          } catch (error) {
          }
        }

        this.notifyListeners();
      } else {
        // Create default structure if doesn't exist
        this.createDefaultSettings();
      }
    });

    return unsubscribe;
  }

  // Get default fee structure
  getDefaultStructure() {
    return {
      tiers: [
        { maxAmount: 150, fee: 7.5, type: 'fixed' },
        { maxAmount: 1000, fee: 5, type: 'percentage' },
        { maxAmount: 5000, fee: 4.5, type: 'percentage' },
        { maxAmount: null, fee: 4, type: 'percentage' }
      ],
      minFee: 7.5,
      lastUpdated: new Date(),
      updatedBy: 'system'
    };
  }

  // Create default settings in Firebase
  async createDefaultSettings() {
    const feeDoc = doc(db, 'feeSettings', 'cardspay-config');
    const defaultSettings = {
      default: this.getDefaultStructure(),
      customerOverrides: {}
    };

    await setDoc(feeDoc, defaultSettings);
    this.defaultFees = defaultSettings.default;
    this.customerOverrides = {};
  }

  // Calculate fee for a specific customer
  calculateFee(amount, customerEmail = null) {
    // Only log when there's an actual email (skip test calculator)
    if (customerEmail) {
    }

    // Check for customer override first
    if (customerEmail && this.customerOverrides[customerEmail]) {
      const result = this.calculateCustomerFee(amount, this.customerOverrides[customerEmail]);
      return result;
    }

    // Use default fee structure
    const result = this.calculateDefaultFee(amount);
    if (customerEmail) {
    }
    return result;
  }

  // Calculate fee using default structure
  calculateDefaultFee(amount) {
    if (!this.defaultFees) {
      // Fallback if not loaded yet
      const structure = this.getDefaultStructure();
      return this.applyFeeStructure(amount, structure);
    }

    return this.applyFeeStructure(amount, this.defaultFees);
  }

  // Apply fee structure to amount
  applyFeeStructure(amount, structure) {
    const { tiers, minFee = 7.5 } = structure;

    // Find applicable tier
    const tier = tiers.find(t => !t.maxAmount || amount <= t.maxAmount);

    if (!tier) {
      // Fallback to last tier
      const lastTier = tiers[tiers.length - 1];
      return this.calculateTierFee(amount, lastTier, minFee);
    }

    return this.calculateTierFee(amount, tier, minFee);
  }

  // Calculate fee for a specific tier
  calculateTierFee(amount, tier, minFee) {
    let feeAmount;

    if (tier.type === 'fixed') {
      feeAmount = tier.fee;
    } else if (tier.type === 'percentage') {
      feeAmount = amount * (tier.fee / 100);
    } else {
      feeAmount = minFee;
    }

    // Apply minimum fee only if fee is not explicitly 0
    // If tier.fee is exactly 0, don't apply minimum fee
    if (tier.fee === 0) {
      feeAmount = 0;
    } else {
      feeAmount = Math.max(feeAmount, minFee);
    }

    // NEW LOGIC: Fee is deducted from amount, not added
    // User pays "amount", fee is deducted, rest goes to card
    const cardAmount = amount - feeAmount;

    return {
      depositAmount: parseFloat(cardAmount.toFixed(2)),  // What goes to card
      feeAmount: parseFloat(feeAmount.toFixed(2)),       // Fee charged
      feePercentage: parseFloat(((feeAmount / amount) * 100).toFixed(2)),
      totalAmount: parseFloat(amount.toFixed(2))         // What user pays (same as input)
    };
  }

  // Calculate fee for customer with override
  calculateCustomerFee(amount, override) {
    // New: Support for custom tiers
    if (override.type === 'custom_tiers') {
      // Use customer's custom tier structure
      const customStructure = {
        tiers: override.tiers || [],
        minFee: override.minFee || 0
      };
      const result = this.applyFeeStructure(amount, customStructure);
      result.customFee = true;
      result.customType = 'tiers';
      return result;
    } else if (override.type === 'fixed_percentage') {
      const feeAmount = amount * (override.fee / 100);
      const cardAmount = amount - feeAmount;
      return {
        depositAmount: parseFloat(cardAmount.toFixed(2)),
        feeAmount: parseFloat(feeAmount.toFixed(2)),
        feePercentage: override.fee,
        totalAmount: parseFloat(amount.toFixed(2)),
        customFee: true
      };
    } else if (override.type === 'fixed_amount') {
      const cardAmount = amount - override.fee;
      return {
        depositAmount: parseFloat(cardAmount.toFixed(2)),
        feeAmount: override.fee,
        feePercentage: parseFloat(((override.fee / amount) * 100).toFixed(2)),
        totalAmount: parseFloat(amount.toFixed(2)),
        customFee: true
      };
    } else if (override.type === 'discount') {
      // Apply discount to default fee
      const defaultFee = this.calculateDefaultFee(amount);
      const discountedFee = defaultFee.feeAmount * (1 - override.discount / 100);
      const cardAmount = amount - discountedFee;
      return {
        depositAmount: parseFloat(cardAmount.toFixed(2)),
        feeAmount: parseFloat(discountedFee.toFixed(2)),
        feePercentage: parseFloat(((discountedFee / amount) * 100).toFixed(2)),
        totalAmount: parseFloat(amount.toFixed(2)),
        customFee: true,
        discount: override.discount
      };
    }

    // Fallback to default
    return this.calculateDefaultFee(amount);
  }

  // Update default fee structure (admin only)
  async updateDefaultFees(newStructure, updatedBy) {
    const feeDoc = doc(db, 'feeSettings', 'cardspay-config');
    await updateDoc(feeDoc, {
      default: {
        ...newStructure,
        lastUpdated: new Date(),
        updatedBy
      }
    });
  }

  // Set customer override (admin only)
  async setCustomerOverride(customerEmail, override, updatedBy) {
    const firebaseKey = customerEmail.replace(/\./g, ',');
    const overrideData = {
      ...override,
      lastUpdated: new Date().toISOString(),
      updatedBy
    };

    const feeDoc = doc(this.db, 'fee_configuration', 'main');
    try {
      await updateDoc(feeDoc, {
        [`customerOverrides.${firebaseKey}`]: overrideData
      });

      // Update local cache immediately with original email
      this.customerOverrides[customerEmail] = overrideData;
    } catch (error) {
      throw error;
    }
  }

  // Remove customer override (admin only)
  async removeCustomerOverride(customerEmail) {
    const feeDoc = doc(db, 'feeSettings', 'cardspay-config');

    // Replace dots with underscores for Firebase key
    const firebaseKey = customerEmail.replace(/\./g, '_');

    const docSnap = await getDoc(feeDoc);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Try to delete both formats (with dots and with underscores)
      delete data.customerOverrides[customerEmail];
      delete data.customerOverrides[firebaseKey];
      await setDoc(feeDoc, data);

      // Update local cache
      delete this.customerOverrides[customerEmail];
      this.notifyListeners();
    }
  }

  // Get all customer overrides (admin only)
  async getCustomerOverrides() {
    const feeDoc = doc(db, 'feeSettings', 'cardspay-config');
    const docSnap = await getDoc(feeDoc);

    if (docSnap.exists()) {
      return docSnap.data().customerOverrides || {};
    }
    return {};
  }

  // Get current data
  getCurrentData() {
    return {
      defaultFees: this.defaultFees,
      customerOverrides: this.customerOverrides
    };
  }

  // Subscribe to fee changes
  subscribe(callback) {
    this.listeners.push(callback);
    // Immediately call callback with current data
    callback(this.getCurrentData());
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  // Notify listeners of changes
  notifyListeners() {
    const data = this.getCurrentData();
    this.listeners.forEach(callback => callback(data));
  }
}

// Create singleton instance
const feeManager = new FeeManager();

export default feeManager;
